import { useState } from "react";
import {
  Upload as UploadIcon,
  Check,
  AlertCircle,
  RotateCcw,
  X as XIcon,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Video,
  Music,
  FileText,
  Loader2,
  Trash2,
} from "lucide-react";
import useUploads from "../hooks/useUploads";
import { retry, dismiss } from "../lib/uploadManager";

// [Iter 339 — 2026-02-25] Belt-and-braces stringifier. IndexedDB rows
// persisted BEFORE the Iter 339 fix may still carry `error` as a raw
// object (validation error dict). Rendering that as a React child
// throws "Objects are not valid as a React child" and crashes the
// whole app — Dirk 2026-02-25: "every time I click on it, the app
// crashes". This helper NEVER returns a non-string.
function safeErrorText(err) {
  if (err == null) return "Upload failed.";
  if (typeof err === "string") return err;
  if (typeof err === "number" || typeof err === "boolean") return String(err);
  if (typeof err === "object") {
    if (Array.isArray(err) && err.length && err[0] && typeof err[0].msg === "string") {
      return err[0].msg;
    }
    if (typeof err.msg === "string") return err.msg;
    if (typeof err.detail === "string") return err.detail;
    return "Server rejected the upload.";
  }
  return "Upload failed.";
}

/**
 * Wave 26.8 — Global "Uploads" dock.
 *
 * Mounted ONCE at app root (Layout.jsx) so it survives every route
 * change. Subscribes to the singleton upload manager and shows a
 * collapsible footer pill with "Uploading 2/3 · 47%" when ≥1 upload
 * is in flight. Hidden completely when there are no items.
 *
 * Why this matters: the user explicitly stated "if a user uploads
 * the photos, he sees the uploading, exits the screen and they
 * don't continue uploading, this app will fail." This dock makes
 * the in-progress state visible from ANYWHERE in the app, so the
 * user always knows their photos are still travelling.
 *
 * Click to expand the panel and see per-file progress + retry.
 */
export default function UploadDock() {
  const items = useUploads();
  const [expanded, setExpanded] = useState(false);

  if (!items.length) return null;

  const inFlight = items.filter((i) => i.status === "uploading" || i.status === "queued" || i.status === "attaching");
  const failed = items.filter((i) => i.status === "error" || i.status === "attach_failed");
  const done = items.filter((i) => i.status === "done");

  // Average progress across in-flight items (queued counts as 0).
  const avgProgress = inFlight.length > 0
    ? Math.round(inFlight.reduce((sum, i) => sum + (i.progress || 0), 0) / inFlight.length)
    : 100;

  const summary = inFlight.length > 0
    ? `Uploading ${done.length + 1}/${items.length} · ${avgProgress}%`
    : failed.length > 0
      ? `${failed.length} upload${failed.length === 1 ? "" : "s"} failed`
      : `${done.length} upload${done.length === 1 ? "" : "s"} complete`;

  const tone =
    failed.length > 0 ? "border-rose-500/40 bg-rose-500/10 text-rose-100"
    : inFlight.length > 0 ? "border-[#1FB6C1]/40 bg-[#0a0b0e]/95 text-white"
    : "border-emerald-500/40 bg-emerald-500/10 text-emerald-100";

  return (
    <div
      className="fixed bottom-[calc(64px+env(safe-area-inset-bottom))] md:bottom-4 right-3 md:right-4 z-[60] w-[260px] sm:w-[320px]"
      data-testid="upload-dock"
    >
      <div className={`rounded-md border ${tone} backdrop-blur shadow-lg overflow-hidden`}>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/[0.04]"
          data-testid="upload-dock-toggle"
        >
          {inFlight.length > 0
            ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
            : failed.length > 0
              ? <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              : <Check className="w-3.5 h-3.5 shrink-0" />
          }
          <span className="text-xs font-medium flex-1 text-left truncate" data-testid="upload-dock-summary">{summary}</span>
          {expanded ? <ChevronDown className="w-3.5 h-3.5 opacity-70" /> : <ChevronUp className="w-3.5 h-3.5 opacity-70" />}
        </button>
        {/* Slim progress strip when collapsed and uploads are in flight */}
        {!expanded && inFlight.length > 0 && (
          <div className="h-0.5 bg-[#1FB6C1]/15">
            <div
              className="h-full bg-[#1FB6C1] transition-all duration-300"
              style={{ width: `${avgProgress}%` }}
            />
          </div>
        )}
        {expanded && (
          <>
            {/* [Iter 339] Escape hatch — one-click clear all failed uploads.
                Also wipes their persisted IndexedDB rows so they don't
                resurrect on next boot. Critical when a run of 33 dead
                uploads has stuck the queue. */}
            {failed.length > 0 && (
              <div className="border-t border-white/10 px-3 py-2 flex items-center justify-between gap-2 text-[10px]">
                <span className="text-rose-200/80">
                  {failed.length} failed · retry individually or clear all
                </span>
                <button
                  type="button"
                  onClick={() => {
                    // eslint-disable-next-line no-alert
                    if (window.confirm(`Dismiss ${failed.length} failed upload${failed.length === 1 ? "" : "s"}? This wipes the queue — you'll need to re-select the photos to upload them again.`)) {
                      failed.forEach((f) => dismiss(f.id));
                    }
                  }}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-500/15 hover:bg-rose-500/25 text-rose-100 border border-rose-500/30"
                  data-testid="upload-dock-dismiss-all-failed"
                >
                  <Trash2 className="w-3 h-3" /> Dismiss all failed
                </button>
              </div>
            )}
            <div className="max-h-[40vh] overflow-y-auto divide-y divide-white/5 border-t border-white/10">
              {items.map((it) => (
                <UploadRow key={it.id} item={it} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function UploadRow({ item }) {
  const Icon =
    item.kind === "image" ? ImageIcon
    : item.kind === "video" ? Video
    : item.kind === "audio" ? Music
    : FileText;
  const isFailed = item.status === "error" || item.status === "attach_failed";
  const isDone = item.status === "done";
  const isInFlight = item.status === "uploading" || item.status === "queued" || item.status === "attaching";
  return (
    <div className="px-3 py-2 text-xs" data-testid={`upload-dock-row-${item.id}`}>
      <div className="flex items-start gap-2">
        <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-70" />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate" title={item.filename}>{item.label || item.filename}</div>
          <div className="text-[10px] opacity-70 mt-0.5">
            {isFailed ? <span className="text-rose-300">{safeErrorText(item.error)}</span>
              : item.status === "queued" ? "Queued…"
              : item.status === "uploading" ? `${item.progress}% · ${prettySize(item.size)}`
              : item.status === "attaching" ? "Linking to record…"
              : "Done"}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isFailed && (
            <button
              type="button"
              onClick={() => retry(item.id)}
              className="p-1 rounded text-[#1FB6C1] hover:bg-white/[0.06]"
              title="Retry"
              data-testid={`upload-dock-retry-${item.id}`}
            >
              <RotateCcw className="w-3 h-3" />
            </button>
          )}
          {(isFailed || isDone) && (
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              className="p-1 rounded text-[#647087] hover:bg-white/[0.06] hover:text-white"
              title="Dismiss"
              data-testid={`upload-dock-dismiss-${item.id}`}
            >
              <XIcon className="w-3 h-3" />
            </button>
          )}
          {isInFlight && item.status === "uploading" && (
            <span className="text-[10px] font-mono text-[#1FB6C1]">{item.progress}%</span>
          )}
        </div>
      </div>
      {isInFlight && (
        <div className="h-0.5 bg-white/5 mt-1.5 rounded">
          <div
            className="h-full bg-[#1FB6C1] transition-all duration-200 rounded"
            style={{ width: `${item.progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

function prettySize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
